import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SpeciesDiagnosisDropdown } from 'src/app/medical-history/models/species-diagnosis-dropdown.model';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { Languages } from 'src/app/shared/translation/languages.enum';
import { CustomTranslationService } from 'src/app/shared/translation/custom-translation.service';
import { PrescriptionHexRow } from 'src/app/medical-history/models/prescription-hex-row';
import { HexAdministration, HexDiagnosis, HexServingMode } from 'src/app/api/models';

@Component({
  selector: 'diana-diagnosies-with-key',
  templateUrl: './diagnosies-with-key.component.html',
  styleUrls: ['./diagnosies-with-key.component.scss'],
  standalone: false
})
export class DiagnosiesWithKeyComponent implements OnInit {

  @Input() formVisit!: FormGroup;
  @Input() hexDiagnoses: Array<HexDiagnosis> | undefined;
  @Input() showTitle = false;
  @Input() isReadOnly = false;
  @Input() diagnosisList: SpeciesDiagnosisDropdown[] = [];
  @Input() treatmentPrescriptions: PrescriptionHexRow[] = [];
  @Input() isDiagnosisRequired = false; // based on this flag it will set the treatment table
  @Input() servingModes: Array<HexServingMode> | undefined = [];
  @Input() applicationTypes: Array<HexAdministration> = [];
  @Input() uniqueId: string | number = '';
  @Output() typeIsChanged = new EventEmitter<boolean>();
  @Output() showCalendarEvent = new EventEmitter<FormControl>();

  currentLanguage = Languages.de;

  get diagnosis(): FormArray {
    return this.formVisit.get('hexDiagnosis') as FormArray;
  }
  get visitAtControl(): FormControl {
    return this.formVisit.get('date') as FormControl;
  }

  get diagnosisFormArray(): any {
    return this.diagnosis.controls;
  }
  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
    private translationService: CustomTranslationService,
  ) {
    this.currentLanguage = this.localStorageService.getLanguage() || Languages.de;

    this.translationService.onLangChange.subscribe(() => {
      this.currentLanguage = this.localStorageService.getLanguage() || Languages.de;
    });

  }

  ngOnInit(): void {
    if (this.hexDiagnoses && this.hexDiagnoses.length > 0) {
      this.hexDiagnoses.map((hexDiagnose: HexDiagnosis) => {
        this.diagnosis.push(this.createItem(hexDiagnose));
      })
    }
    if (this.diagnosis.length === 0) {
      this.diagnosis.push(this.createItem());
    }
    // Required because the initialiazation of dosages in this.formVisit.get('hexDiagnosis') was marking it as dirty and it happens only after the treatment table is generated
    // The pristine is used to check if there is changes and loading existing prescription was causing the this.formVisit.get('hexDiagnosis') to be dirty
    setTimeout(() => {
      this.diagnosis.markAsPristine();
    })
  }

  public handleTypeChange(): void {
    this.typeIsChanged.emit();
  }
  // Create a new FormGroup for an item
  createItem(initialDiagnosis: any = undefined): FormGroup {
    const newDiagnosisForm = {
      initialSelection: new FormControl(initialDiagnosis?.id || ''),
      code: new FormControl({ value: '', disabled: this.isReadOnly }),
      diagnosisLevel1: new FormControl({ value: '', disabled: this.isReadOnly }),
      diagnosisLevel2: new FormControl({ value: '', disabled: true }),
      treatmentsList: []
    }
    if (this.isDiagnosisRequired) {
      newDiagnosisForm.diagnosisLevel1.setValidators(Validators.required)
      newDiagnosisForm.diagnosisLevel2.setValidators(Validators.required)
    }
    // Using visitAt to check if the prescription is readonly and sets to disable the diagnostic dropdowns
    if (this.visitAtControl.disabled) {
      newDiagnosisForm.diagnosisLevel1.disable();
      newDiagnosisForm.code.disable();
      this.isReadOnly = true;
    }
    return this.fb.group(newDiagnosisForm);
  }

  // Create a new FormGroup for an item
  addExistingItem(diagnosisLevel1: SpeciesDiagnosisDropdown | undefined = undefined, diagnosisLevel2: SpeciesDiagnosisDropdown | undefined = undefined): FormGroup {
    return this.fb.group({
      code: new FormControl(''),
      diagnosisLevel1: new FormControl(diagnosisLevel1 || ''),
      diagnosisLevel2: new FormControl(diagnosisLevel2 || ''),
      treatmentsList: new FormControl([]),
    });
  }

  addDiagnosis() {
    if (this.diagnosis.length < 3) {
      this.diagnosis.push(this.createItem());
    }
  }

  /**
   * To cast AbstractControl as FormGroup
   * @param item AbstractControl
   * @returns returns  AbstractControl as FormGroup
   */
  getFormGroup(item: AbstractControl): FormGroup {
    return item as FormGroup;
  }

  /**
   * Button delete action of the diagnosis
   * @param diagnosisIndex index of the diagnosis to remove
   */
  public removeDiagnosis(diagnosisIndex: number): void {
    const treatmentsToMove: PrescriptionHexRow[] = this.diagnosis.controls[diagnosisIndex].get('treatmentsList')?.value;
    // if exists treatments on the diagnosis, it moves them
    if (treatmentsToMove && treatmentsToMove.length > 0) {
      if (diagnosisIndex === 0) {
        this.moveTreatmentsOnDelete(diagnosisIndex + 1, treatmentsToMove);
      } else {
        this.moveTreatmentsOnDelete(0, treatmentsToMove);
      }
    }
    this.diagnosis.removeAt(diagnosisIndex);
    this.diagnosis.markAsDirty();
  }

  /**
   * Moves treatment based on index
   * @param diagnosisIndex Index on where to move
   * @param treatmentsToMove The treatment to move
   */
  private moveTreatmentsOnDelete(diagnosisIndex: number, treatmentsToMove: PrescriptionHexRow[]): void {
    const currentTreatments: PrescriptionHexRow[] = this.diagnosis.controls[diagnosisIndex].get('treatmentsList')?.value || [];
    treatmentsToMove.map(treatment => {
      treatment.diagnosisId = this.diagnosis.controls[diagnosisIndex].get('diagnosisLevel2')?.value;
      return treatment;
    })
    this.diagnosis.controls[diagnosisIndex].get('treatmentsList')?.setValue([...treatmentsToMove, ...currentTreatments]);
    // to disable dosis component after the treatments are moved in the case of isIsabv is false and isTaps is false
    this.diagnosis.controls[diagnosisIndex].get('treatmentsList')?.value.map((treatment: PrescriptionHexRow) => {
      if (treatment.isIsabv?.value === false && treatment.isTaps?.value === false) {
        treatment.dosagesFormGroup?.disable();
      }
    });
    this.diagnosis.controls[diagnosisIndex].get('treatmentsList')?.updateValueAndValidity();
  }

  public handleShowCalendar(date: any): void {
    this.showCalendarEvent.emit(date)
  }
}
